import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  FaUser, 
  FaEnvelope, 
  FaPhoneAlt, 
  FaLock, 
  FaUserTag, 
  FaArrowLeft, 
  FaSave, 
  FaSpinner 
} from 'react-icons/fa';
import { getUserById, createUser, updateUser, adminUpdateUser } from './userService';
import DashboardLayout from '../../layouts/DashboardLayout';
import Input from '../../components/shared/Input';
import Select from '../../components/shared/Select';
import MaskedInput from '../../components/shared/MaskedInput';
import {
  FormContainer,
  FormSection,
  FormHeader,
  BackButton,
  FormGrid,
  FormField,
  SubmitButton
} from '../../components/styled/FormComponents';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import FeedbackModal from '../../components/shared/ModalNotification';
import { useAuth } from '../auth/AuthContext';

const UserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    role: 'user'
  });
  
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [errors, setErrors] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);

  // Fetch user data if in edit mode
  useEffect(() => {

    console.log("ID: ", id);
    console.log("Is edit mode? ", isEditMode);
    console.log("Is Admin? ", isAdmin);
    console.log("Current user id: ", currentUser.userId);
    console.log("Current role: ", currentUser.role);
    console.log("Current user: ", currentUser);

    if (!isEditMode) return;
    
    if(!isAdmin && id !== String(currentUser.userId)){
      navigate(`/users/edit${currentUser.userId}`, {replace: true});
      return;
    }

    console.log("Passou isAdmin");

    const fetchUser = async () => {
      try {
        setInitialLoading(true);
        const targetId = isAdmin ? id : currentUser.userId;
        console.log("Target ID: ",targetId);
        const userData = await getUserById(targetId);

        console.log("Editando: ", userData);

        setFormData({
          ...formData,
          username: userData.username || '',
          email: userData.email || '',
          phoneNumber: userData.phoneNumber || '',
          role: userData.role || 'user',
          // Don't set password fields when editing
          password: '',
          confirmPassword: ''
        });

        console.log("Form data: ", formData);
      } catch (error) {
            console.error('Error fetching user:', error);
            setFeedbackMessage('Failed to load user data. Please try again.');
            setFeedbackSuccess(false);
            setShowFeedback(true);
          } finally {
            setInitialLoading(false);
          }
    };

    fetchUser();
  }, [id, isEditMode, isAdmin, currentUser.id, navigate]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    if (!isEditMode) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
      
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    } else if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setLoading(true);
    
    try {
      const userData = {
        username: formData.username,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        role: formData.role
      };
      
      // Only include password if it's provided (required for new users)
      if (formData.password) {
        userData.password = formData.password;
      }
      
      let response;
      if (isEditMode) {
        if (isAdmin) {
          // Admin can update any user
          response = await adminUpdateUser(id, userData);
          console.log(response);
        } else {
          // Regular users can only update their own profile
          response = await updateUser(userData);
        }
        
        setFeedbackMessage('User updated successfully');
      } else {
        // Creating a new user
        response = await createUser(userData);
        setFeedbackMessage('User created successfully');
      }
      
      setFeedbackSuccess(true);
      setShowFeedback(true);
    } catch (error) {
      console.error('Error saving user:', error);
      setFeedbackMessage(error.response?.data?.message || 'Failed to save user. Please try again.');
      setFeedbackSuccess(false);
      setShowFeedback(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    
    // Clear the error for this field when user starts typing
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: null
      }));
    }
  };

  const handleFeedbackComplete = () => {
    setShowFeedback(false);
    if (feedbackSuccess) {
      navigate('/users');
    }
  };

  if (initialLoading) {
    return (
      <DashboardLayout>
        <LoadingSpinner />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <FormContainer>
        <FormHeader>
          <BackButton onClick={() => navigate('/users')}>
            <FaArrowLeft /> Back to Users
          </BackButton>
          <h2>{isEditMode ? 'Edit User' : 'Create User'}</h2>
        </FormHeader>

        <form onSubmit={handleSubmit}>
          <FormSection>
            <h3>User Information</h3>
            <FormGrid>
              <FormField index={0}>
                <Input
                  label="Username"
                  icon={<FaUser />}
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Enter username"
                  error={errors.username}
                  required
                />
              </FormField>

              <FormField index={1}>
                <Input
                  label="Email"
                  icon={<FaEnvelope />}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  error={errors.email}
                  required
                />
              </FormField>

              <FormField index={2}>
                <MaskedInput
                  label="Phone Number"
                  icon={<FaPhoneAlt />}
                  name="phoneNumber"
                  mask="(999) 999-9999"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                />
              </FormField>
              
              {isAdmin && (
                <FormField index={3}>
                  <Select
                    label="Role"
                    icon={<FaUserTag />}
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                  >
                    <option value="user">User</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </Select>
                </FormField>
              )}
            </FormGrid>
          </FormSection>

          <FormSection>
            <h3>{isEditMode ? 'Change Password (Optional)' : 'Password'}</h3>
            <FormGrid>
              <FormField index={4}>
                <Input
                  label="Password"
                  icon={<FaLock />}
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={isEditMode ? "Leave blank to keep current password" : "Enter password"}
                  error={errors.password}
                  required={!isEditMode}
                />
              </FormField>

              <FormField index={5}>
                <Input
                  label="Confirm Password"
                  icon={<FaLock />}
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm password"
                  error={errors.confirmPassword}
                  required={!isEditMode}
                />
              </FormField>
            </FormGrid>
          </FormSection>

          <SubmitButton type="submit" disabled={loading}>
            {loading ? (
              <>
                <FaSpinner className="icon-spin" />
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <FaSave />
                {isEditMode ? 'Update User' : 'Create User'}
              </>
            )}
          </SubmitButton>
        </form>
      </FormContainer>

      <FeedbackModal
        isOpen={showFeedback}
        message={feedbackMessage}
        success={feedbackSuccess}
        onComplete={handleFeedbackComplete}
      />
    </DashboardLayout>
  );
};

export default UserForm;